import { FormGroup, Input } from '@snsw/react-component-library';
import { FocusEventHandler, ReactNode } from 'react';
import { type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useFieldErrorMessage } from './hooks';
import { FormFieldProps } from './types';

export interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends FormFieldProps<TFieldValues, TName> {
  label?: string;
  helpMessage?: ReactNode;
  type?: 'text' | 'number' | 'date' | 'email' | 'tel';
  inputWidth?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  className?: string;
  onBlurTransform?: (next: string) => string | undefined;
}

export const FormInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: FormInputProps<TFieldValues, TName>
) => {
  const {
    id,
    name,
    label,
    helpMessage,
    errorMode,
    required,
    optional,
    className,
    rules,
    shouldUnregister,
    onBlurTransform,
    ...inputProps
  } = props;

  const {
    field: { value, ref, name: fieldName, ...fieldProps },
    fieldState,
  } = useController<TFieldValues, TName>({
    name,
    rules,
    shouldUnregister,
  });

  const errorMessage = useFieldErrorMessage(fieldName, errorMode);

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    const trimmedValue = e.target.value.trim();
    const formattedValue = onBlurTransform ? onBlurTransform(trimmedValue) : trimmedValue;
    if (e.target.value !== formattedValue) {
      fieldProps.onChange(formattedValue);
    }

    // Propagate
    fieldProps.onBlur();
  };

  return (
    <FormGroup
      id={id || name}
      hasError={fieldState.error != null}
      errorMessage={errorMessage}
      label={label}
      helpMessage={helpMessage}
      isRequired={required}
      isOptional={optional}
      className={className}
    >
      <Input
        value={value ?? ''}
        {...inputProps}
        {...fieldProps}
        id={id}
        name={fieldName}
        inputRef={ref}
        onBlur={handleBlur}
      />
    </FormGroup>
  );
};

FormInputField.defaultProps = {
  type: 'text',
  size: 'small',
};
